import * as RadPopover from '@radix-ui/react-popover'
import { useAtom } from '@reatom/npm-react'
import React, { useCallback, useState } from 'react'
import styled, { keyframes } from 'styled-components'

import { useHapticFeedback } from '@blade/ui-kit'
import {
  ArrowDown2Icon,
  ArrowUp2Icon,
  OneColLayoutIconExact,
  ThreeColsLayoutIconExact,
  TwoColsLayoutIconExact,
} from '@root/shared/ui-kit'

import { exploreLayoutTypeAtom } from '../../atoms/reatom-explore-layout'
import { ExploreLayout } from '../../model'

import type { IconProps } from '@root/shared/ui-kit/icons/create-icon'

const ExploreLayoutIconMap: Record<ExploreLayout.Type, React.FC<IconProps>> = {
  [ExploreLayout.Type.OneColumn]: OneColLayoutIconExact,
  [ExploreLayout.Type.TwoColumns]: TwoColsLayoutIconExact,
  [ExploreLayout.Type.ThreeColumns]: ThreeColsLayoutIconExact,
}

export const ExploreLayoutSelect = () => {
  const [exploreLayout, setExploreLayout] = useAtom(exploreLayoutTypeAtom)
  const [open, setOpen] = useState(false)
  const haptics = useHapticFeedback()

  const handleSelect = useCallback(
    (item: ExploreLayout.Type) => {
      haptics.impactOccurred('light')
      setExploreLayout(item)
      setOpen(false)
    },
    [haptics, setExploreLayout],
  )

  const handleTriggerClick = useCallback(() => {
    haptics.impactOccurred('light')
    setOpen((x) => !x)
  }, [haptics])

  const LayoutIcon = ExploreLayoutIconMap[exploreLayout]

  return (
    <Root open={open} onOpenChange={setOpen}>
      <Trigger asChild>
        <Button type="button" onClick={handleTriggerClick}>
          <Value>
            <LayoutIcon width={21} height={16} />
          </Value>
          <ArrowIcon>
            <ArrowDown2Icon size={12} />
          </ArrowIcon>
        </Button>
      </Trigger>
      <RadPopover.Portal>
        <Content side="bottom" align="center">
          <Button onClick={handleTriggerClick} type="button" aria-expanded>
            <Value>
              <LayoutIcon width={21} height={16} />
            </Value>
            <ArrowIcon>
              <ArrowUp2Icon size={12} />
            </ArrowIcon>
          </Button>
          {ExploreLayout.types.map((item) => {
            const Icon = ExploreLayoutIconMap[item]
            return (
              <Item key={item} onClick={() => handleSelect(item)}>
                <OptionItem $active={item === exploreLayout}>
                  <Icon width={22} />
                </OptionItem>
              </Item>
            )
          })}
        </Content>
      </RadPopover.Portal>
    </Root>
  )
}

const contentShow = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`

const Root = styled(RadPopover.Root)``
const Trigger = styled(RadPopover.Trigger)``

const Button = styled.button`
  all: unset;

  border: 1px solid var(--backgroundA-3);
  display: flex;
  padding: 2px 9px 2px 2px;
  border-radius: 20px;
  gap: 5px;
  align-items: center;
  cursor: pointer;
  position: relative;

  transition: all 0.15s ease-in-out;

  &:hover {
    background: var(--backgroundA-2);
  }

  &[aria-expanded='true'] {
    border: 1px solid var(--backgroundA-3);

    &:hover {
      background: transparent;
    }
  }
`

const ArrowIcon = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

const Value = styled.div`
  color: var(--text-11);

  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 32px;
  min-height: 20px;
  padding: 0 1px 1px 7px;
  flex-shrink: 0;

  & > svg {
    display: block;
    flex-shrink: 0;
  }
`

const Content = styled(RadPopover.Content)`
  background: var(--appearance-1);
  box-shadow: 2px 2px 9px 1px var(--appearanceA-3);
  margin-top: -20px;
  z-index: 2;
  padding: 2px;
  gap: 2px;
  display: flex;
  flex-direction: column;

  border-radius: 16px;
  animation-duration: 200ms;
  animation-name: ${contentShow};
`

const Item = styled.div`
  background: var(--backgroundA-2);
  padding: 2px;

  border-radius: 20px;
  border: 0;
  width: 100%;

  transition: all 0.15s ease-in-out;

  &:hover {
    background: var(--backgroundA-4);
    cursor: pointer;
  }
`

const OptionItem = styled.div<{ $active: boolean }>`
  color: ${({ $active }) => ($active ? 'var(--text-11)' : 'var(--text-9)')};
  width: 100%;

  display: flex;
  align-items: center;
  justify-content: center;
  gap: 5px;
  padding: 5px 7px;

  font-size: 11px;
  font-weight: 600;
`
